import { ThreadViewModel } from '../../models/thread-view.model';
import { createSortCallback } from '@anecdotes/utils';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { createComposedResourceId } from '../../utils';
import { CommentingFeatureState, featureKey } from '../state';
import { CommentingPanelState } from '../reducers';
import { Dictionary } from '@ngrx/entity';
import { CommentFilterOptionDefinition, CommentingResourceModel, ThreadSort } from '../../models';
import { UserModel } from '@anecdotes/commenting';

export const featureSelector = createSelector(
  createFeatureSelector<CommentingFeatureState>(featureKey),
  (state: CommentingFeatureState) => state.commentingPanelState,
);

export const selectHasUniqueSingleThreadDesign = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.hasUniqueSingleThreadDesign,
);

export const selectShouldDisplayBackButton = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.shouldDisplayBackButton,
);

export const selectCommonLogData = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.commonLogData,
);

export const selectResourcesDictionary = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.resources.entities,
);

export const selectResources = createSelector(
  selectResourcesDictionary,
  (entities: Dictionary<CommentingResourceModel>) => Object.values(entities),
);

export const selectSelectedSortOption = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.selectedSortOption,
);

export const selectAreThreadsLoaded = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.areThreadsLoaded,
);

export const selectThreadViewModels = createSelector(
  featureSelector,
  selectSelectedSortOption,
  (state: CommentingPanelState, sortingOption: ThreadSort) => {
    const threadViewModels = [...(state.threadViewModels || [])];

    if (!sortingOption?.field) {
      return threadViewModels;
    }

    const sortCallback = createSortCallback(
      (tvm: ThreadViewModel) => tvm[sortingOption?.field],
      sortingOption?.direction,
    );

    return threadViewModels?.sort(sortCallback);
  },
);

export const selectIsInitialized = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.isInitialized,
);

export const selectResourceType = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.resourceType,
);

export const selectThreadCreationFor = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.threadCreationFor,
);

export const selectFilterOptions = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.filterOptions,
);

export const selectSortOptions = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.sortOptions,
);

export const selectSelectedFilterOption = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.selectedFilterOption,
);

export const selectDisplayedSingleThreadResourceId = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.selectedSingleThreadResourceId,
);

export const selectDisplayedThreadViewModels = createSelector(
  selectThreadViewModels,
  selectSelectedFilterOption,
  selectDisplayedSingleThreadResourceId,
  selectFilterOptions,
  (
    threadViewModels: ThreadViewModel[],
    selectedFilterOption: CommentFilterOptionDefinition,
    singleThreadResourceId: string,
    filterOptions: CommentFilterOptionDefinition[]
  ) =>
    threadViewModels?.filter((tvm) => {
      if (singleThreadResourceId) {
        const isEqualToSelectedResource = singleThreadResourceId === tvm.resourceId;

        return isEqualToSelectedResource && (tvm.isCreation || ((selectedFilterOption || filterOptions[0]).filterCallback(tvm) ?? true));
      }

      return tvm.isCreation || (selectedFilterOption?.filterCallback(tvm) ?? true);
    })
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const createResourceLogDataSelector = (resourceType: string, resourceId: string) =>
  createSelector(
    selectResourcesDictionary,
    (resourcesDictionary: Dictionary<CommentingResourceModel>) => resourcesDictionary[createComposedResourceId(resourceType, resourceId)]?.logData,
  );

export const selectFocusedResources = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.focusedResources,
);

export const selectIsOpened = createSelector(
  featureSelector,
  (state: CommentingPanelState) => state.isOpen,
);

export const selectUsersMap = createSelector(featureSelector, (state) => {
  return new Map<string, UserModel>(
    state.users
      .map(
        (user) =>
          ({
            id: user.uid,
            email: user.email,
            name: `${user.first_name} ${user.last_name}`,
          } as UserModel),
      )
      .map((user) => [user.name, user]),
  );
});
